import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Mui
import { 
  Theme, alpha,
  Box, Paper, Avatar,
  Button, IconButton, Typography, Tooltip, TextField
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
// Utils
import { parseDateTimeToString } from 'app/utilities/DateTime';

import useCommentsContext, { CommentStatuses, IComment } from './useCommentsContext';
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { useAppSelector } from 'app/hooks/useStore';

const MAX_PREVIEW_LENGTH = 150;

type Props = {
  comment: IComment;
  isActive: boolean;
}

export default function CommentItem({ comment, isActive }: Props) {
  const { t } = useTranslation();

  const myUser = useAppSelector(selectMyUser);

  const isContentLong = comment.content.length > MAX_PREVIEW_LENGTH;

  const { setSelectedCommentId, onUpdateComment, onRemoveComment, onChangeCommentStatus } = useCommentsContext();

  const [ isExpanded, setIsExpanded ] = useState(!isContentLong);
  const [ value, setValue ] = useState(comment.content);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const handleDeleteClick = () => {
    onRemoveComment(comment);
  }

  const handleEditClick = () => {
    onChangeCommentStatus(comment.id, CommentStatuses.edited);
    setSelectedCommentId(comment.id);
  }

  const handleCancelClick = () => {
    if ( comment.status === CommentStatuses.added ) {
      onRemoveComment(comment);
      return;
    }
    onChangeCommentStatus(comment.id, CommentStatuses.none);
    setSelectedCommentId(null);
    setValue(comment.content);
  }

  const handleUpdateClick = () => {
    onUpdateComment(comment.id, value);
    setValue(value);
  }

  const displayContent = isExpanded
    ? comment.content
    : `${comment.content.substring(0, MAX_PREVIEW_LENGTH)}...`
  ;

  const isEditing = [CommentStatuses.added, CommentStatuses.edited].includes(comment.status);
  const isActionVisible = comment.createdBy ? comment.createdBy.id === myUser?.id : false;

  const renderContent = () => {
    if ( !isEditing ) return (
      <Box
        sx={{
          height: 'initial',
          overflow: 'initial',
          p: 0,
          whiteSpace: 'break-spaces'
        }}
        className="ql-editor preview"
        dangerouslySetInnerHTML={{ __html: displayContent }}
      />
    );
    return (
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add a comment..."
        margin="none"
        multiline
        minRows={3}
        maxRows={5}
        inputRef={el => {
          if (el) {
            setTimeout(() => el.focus(), 0);
          }
        }}
      />
    )
  }

  const renderActionButtons = () => {
    if ( !isEditing ) return (
      isContentLong ?
        <Box sx={{ pt: 2 }}>
          <Button
            name={`${isExpanded ? "Show Less" : "Show More"} comment ${comment.id}`}
            sx={{
              textTransform: "none",
              p: 0,
              '&:hover': {
                bgcolor: "transparent",
                textDecoration: "underline"
              }
            }}
            onClick={toggleExpanded}
            size="small"
            variant="text"
          >{isExpanded ? "Show Less" : "Show More"}</Button>
        </Box>
      : null
    );
    return (
      <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, pt: 4 }}>
        <Button
          onClick={handleCancelClick}
          variant="text"
        >Cancel</Button>
        <Button
          onClick={handleUpdateClick}
          variant="contained"
        >{comment.status === CommentStatuses.added ? "Add" : "Update"}</Button>
      </Box>
    )
  }

  const handleClick = () => {
    // Select comment
    setSelectedCommentId(comment.id);
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        bgcolor: (theme:Theme) => isActive ? theme.palette.background.paper : alpha(theme.palette.primary.main, 0.075),
        borderRadius: 2,
        p: 4
      }}
      onClick={handleClick}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        mb: 4
      }}>
        <Avatar sx={{ bgcolor: "primary.main" }}>
          {comment.createdBy?.name[0] ?? 'A'}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          {/* User Info */}
          {comment.createdBy ? (
            <Typography variant="subtitle2">{comment.createdBy.name}</Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {parseDateTimeToString(comment.createdOn)}
          </Typography>
        </Box>
        {!isEditing && isActionVisible ? (
          <Box>
            <Tooltip title={t('common.edit')}>
              <IconButton
                name={`Edit comment ${comment.id}`}
                size="small"
                onClick={handleEditClick}
              ><EditIcon /> </IconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <IconButton
                name={`Delete comment ${comment.id}`}
                size="small"
                onClick={handleDeleteClick}
                edge="end"
              ><DeleteIcon /> </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
      {renderContent()}
      {renderActionButtons()}
    </Paper>
  )
}
